import React, { useEffect, useState, useMemo } from 'react';
import { MapContainer, TileLayer, GeoJSON, Marker, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { DevLocationData, ThemeColors } from './types';
import L from 'leaflet';
import { geoCentroid } from 'd3-geo';
import { scaleLinear } from 'd3-scale';

// GeoJSON for world map
const GEO_JSON_URL = "https://raw.githubusercontent.com/holtzy/D3-graph-gallery/master/DATA/world.geojson";

interface DevLocationMapProps {
    allTimeData: DevLocationData[];
    lastYearData: DevLocationData[];
    currentTheme: ThemeColors;
    darkMode: boolean;
}

const MapChart: React.FC<{
    data: DevLocationData[];
    title: string;
    currentTheme: ThemeColors;
    darkMode: boolean;
    mapId: string;
}> = ({ data, title, currentTheme, darkMode, mapId }) => {
    const [geoJsonData, setGeoJsonData] = useState<any>(null);
    const [centroids, setCentroids] = useState<Record<string, [number, number]>>({});

    // Create a map of country names to number of devs for easy lookup
    const dataMap = useMemo(() => {
        return data.reduce((acc, cur) => {
            acc[cur.country] = cur.num_devs;
            return acc;
        }, {} as Record<string, number>);
    }, [data]);

    const maxVal = useMemo(() => Math.max(...data.map(d => d.num_devs), 0), [data]);

    const colorScale = useMemo(() =>
        scaleLinear<string>()
            .domain([0, maxVal || 1])
            .range(darkMode ? ["#2d3748", "#17cac6"] : ["#e2e8f0", "#2D2DF1"]),
        [maxVal, darkMode]
    );

    // Helper to normalize country names for matching
    const normalizeName = (name: string) => {
        if (name === "United States of America" || name === "USA") return "United States";
        if (name === "Turkey") return "Türkiye";
        if (name === "England") return "United Kingdom";
        return name;
    };

    // Identify top 5 countries for labeling
    const top5Countries = useMemo(() => {
        return [...data]
            .sort((a, b) => b.num_devs - a.num_devs)
            .slice(0, 5)
            .map(d => d.country);
    }, [data]);

    // Fetch GeoJSON data and calculate centroids
    useEffect(() => {
        fetch(GEO_JSON_URL)
            .then(response => response.json())
            .then(data => {
                setGeoJsonData(data);

                // Calculate centroids
                const newCentroids: Record<string, [number, number]> = {};
                const tempLayer = L.geoJSON(data);

                tempLayer.eachLayer((layer: any) => {
                    const feature = layer.feature;
                    const name = feature.properties.name;

                    let lat = 0, lng = 0;

                    // Try d3-geo first for better centering
                    try {
                        const centroid = geoCentroid(feature);
                        if (centroid && !isNaN(centroid[0]) && !isNaN(centroid[1])) {
                            // d3-geo returns [lon, lat]
                            lat = centroid[1];
                            lng = centroid[0];
                        }
                    } catch (e) {
                        console.warn(`geoCentroid failed for ${name}`, e);
                    }

                    // Fallback to Leaflet bounds center if d3-geo failed or returned 0,0
                    if (lat === 0 && lng === 0) {
                        if (layer.getBounds) {
                            const center = layer.getBounds().getCenter();
                            lat = center.lat;
                            lng = center.lng;
                        } else if (layer.getLatLng) {
                            const center = layer.getLatLng();
                            lat = center.lat;
                            lng = center.lng;
                        }
                    }

                    if (lat !== 0 || lng !== 0) {
                        newCentroids[name] = [lat, lng];
                    }
                });

                setCentroids(newCentroids);
            })
            .catch(err => console.error("Error loading GeoJSON:", err));
    }, []);

    const style = (feature: any) => {
        const countryName = feature.properties.name;
        let devs = dataMap[countryName] || 0;

        // Try matching with normalized name
        if (devs === 0) {
            const normalized = normalizeName(countryName);
            devs = dataMap[normalized] || 0;
        }

        // Also try reverse matching if data has "United States" but GeoJSON has "USA"
        if (devs === 0) {
            if (countryName === "USA") devs = dataMap["United States"] || 0;
        }

        let fillColor = darkMode ? "#2d3748" : "#F5F4F6";
        if (devs > 0) {
            fillColor = colorScale(devs);
        }

        return {
            fillColor: fillColor,
            weight: 0.5,
            opacity: 1,
            color: darkMode ? "#1a202c" : "#D6D6DA",
            dashArray: '',
            fillOpacity: 1
        };
    };

    const onEachFeature = (feature: any, layer: L.Layer) => {
        const countryName = feature.properties.name;
        let devs = dataMap[countryName] || 0;

        // Try matching with normalized name
        if (devs === 0) {
            const normalized = normalizeName(countryName);
            devs = dataMap[normalized] || 0;
        }
        if (devs === 0 && countryName === "USA") devs = dataMap["United States"] || 0;

        layer.bindTooltip(`${countryName}: ${devs} devs`, {
            direction: 'top',
            sticky: true
        });

        layer.on({
            mouseover: (e) => {
                const layer = e.target;
                layer.setStyle({
                    weight: 0.5,
                    color: darkMode ? "#1a202c" : "#D6D6DA",
                    dashArray: '',
                    fillOpacity: 1,
                    fillColor: darkMode ? "#8BE4E2" : "#8080F3"
                });
                layer.bringToFront();
            },
            mouseout: (e) => {
                const layer = e.target;
                const styleObj = style(feature);
                layer.setStyle(styleObj);
            }
        });
    };

    return (
        <div className="flex flex-col items-center w-full relative">
            <h3 className="text-lg font-bold mb-2" style={{ color: currentTheme.text }}>{title}</h3>
            <div
                className="w-full rounded-lg overflow-hidden relative"
                style={{
                    backgroundColor: currentTheme.chartBg,
                    height: "400px",
                    border: `1px solid ${darkMode ? '#2d3748' : '#e2e8f0'}`
                }}
            >
                <MapContainer
                    center={[20, 0]}
                    zoom={1}
                    scrollWheelZoom={true}
                    style={{ height: '100%', width: '100%', background: 'transparent' }}
                    attributionControl={false}
                    minZoom={1}
                    maxZoom={6}
                >
                    {geoJsonData && (
                        <GeoJSON
                            key={`${mapId}-${darkMode}`}
                            data={geoJsonData}
                            style={style}
                            onEachFeature={onEachFeature}
                        />
                    )}

                    {Object.entries(centroids).map(([geoName, latlng]) => {
                        const normalizedName = normalizeName(geoName);
                        let isTop5 = top5Countries.includes(normalizedName) || top5Countries.includes(geoName);

                        if (isTop5) {
                            const devs = dataMap[normalizedName] || dataMap[geoName] || (geoName === "USA" ? dataMap["United States"] : 0);
                            return (
                                <Marker
                                    key={geoName}
                                    position={latlng}
                                    icon={L.divIcon({
                                        className: 'custom-icon',
                                        html: `<div style="
                                            font-family: system-ui; 
                                            font-size: 15px; 
                                            font-weight: bold; 
                                            color: ${darkMode ? '#ffffff' : '#001324'}; 
                                            text-shadow: ${darkMode ? '0px 0px 2px #001324' : '0px 0px 10px #ffffffff'};
                                            text-align: center;
                                            pointer-events: none;
                                            white-space: nowrap;
                                        ">${devs}</div>`,
                                        iconSize: [1, 1],
                                        iconAnchor: [10, 10]
                                    })}
                                />
                            );
                        }
                        return null;
                    })}
                </MapContainer>
            </div>
        </div>
    );
};

const DevLocationMap: React.FC<DevLocationMapProps> = ({
    allTimeData,
    lastYearData,
    currentTheme,
    darkMode
}) => {
    return (
        <div className="w-full mt-8">
            <h2 className="text-xl md:text-2xl font-bold text-center mb-6" style={{ color: currentTheme.primary }}>
                Global developer distribution
            </h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <MapChart
                    data={allTimeData}
                    title="All Time Developers"
                    currentTheme={currentTheme}
                    darkMode={darkMode}
                    mapId="all-time-map"
                />
                <MapChart
                    data={lastYearData}
                    title="Last 1 Year Developers"
                    currentTheme={currentTheme}
                    darkMode={darkMode}
                    mapId="last-year-map"
                />
            </div>
            <div className="flex justify-end mt-4">
                <div className="flex items-center gap-2">
                    <span className="text-xs" style={{ color: currentTheme.text }}>Powered by:</span>
                    <a href="https://opendevdata.org/" target="_blank" rel="noopener noreferrer">
                        <img
                            src={darkMode ? "/open_dev_data/3.Public Assets/logo-white.svg" : "/open_dev_data/3.Public Assets/logo.svg"}
                            alt="Open Dev Data"
                            className="h-6"
                        />
                    </a>
                </div>
            </div>
        </div>
    );
};

export default DevLocationMap;